import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A tiny global concurrency limiter for image loads. A large pod grid would
 * otherwise fire dozens of requests at once; the upstream (Cloudinary/IPFS,
 * even through our proxy on a cold R2 miss) rate-limits bursts, so many fail.
 * Pacing the START of loads to a handful at a time keeps every request under
 * the throttle — combined with the R2 proxy, the grid fills reliably.
 */

const MAX_CONCURRENT = 5;
let active = 0;
const waiting: Array<() => void> = [];

function acquireSlot(): Promise<() => void> {
  return new Promise((resolve) => {
    const grant = (): void => {
      active += 1;
      let released = false;
      resolve(() => {
        if (released) return;
        released = true;
        active -= 1;
        const next = waiting.shift();
        if (next) next();
      });
    };
    if (active < MAX_CONCURRENT) grant();
    else waiting.push(grant);
  });
}

/**
 * Gate an image's load through the global queue. Returns the src to apply only
 * once a slot is free, plus `onSettled` to call from the img's load/error so
 * the slot frees for the next tile. Releasing on unmount too, so scrolling a
 * windowed grid never strands slots.
 */
export function useQueuedImage(url: string | undefined, onTimeout?: () => void): {
  src: string | undefined;
  onSettled: () => void;
} {
  const [src, setSrc] = useState<string | undefined>(undefined);
  const releaseRef = useRef<(() => void) | null>(null);
  const timeoutHandler = useRef(onTimeout);
  timeoutHandler.current = onTimeout;

  useEffect(() => {
    if (!url) {
      setSrc(undefined);
      return;
    }
    let cancelled = false;
    void acquireSlot().then((release) => {
      if (cancelled) {
        release();
        return;
      }
      // A browser request can stall without load/error; it must not hold the
      // entire wallet queue indefinitely. The image may still finish later.
      const timeout = window.setTimeout(() => { release(); timeoutHandler.current?.(); }, 12000);
      releaseRef.current = () => {
        window.clearTimeout(timeout);
        release();
      };
      setSrc(url);
    });
    return () => {
      cancelled = true;
      releaseRef.current?.();
      releaseRef.current = null;
    };
  }, [url]);

  const onSettled = useCallback(() => {
    releaseRef.current?.();
    releaseRef.current = null;
  }, []);

  return { src, onSettled };
}
